import React, { FC, FormEvent, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import {
  ActionButton,
  Dropdown,
  Icon,
  IDropdownOption as IOfficeDropdownOption,
  Stack,
  Text,
} from "@fluentui/react";
import {
  chartMessages,
  css,
  dateTimeUtils,
  domUtils,
  LineChart,
  LoadingOverlay,
  useTheme,
  useSignals,
} from "cayo.ui";
import { CustomInterval, SavedQuery } from "@cayo/shared/api/types/cayo-graph";
import { IDropdownOption } from "@cayo/shared/api/types/schema";
import { fromSavedQuery } from "@cayo/shared/components/GridContainer/gridModel/savedQuery.portal";
import logger from "@cayo/shared/libs/logger";
import { appSettings } from "@cayo/shared/settings/appSettings";
import { IComponentViewStorage } from "@cayo/shared/settings/viewStorage";
import { AbsoluteEmptyContainer, FullHeightColumn } from "./common";
import WidgetContainer from "./Container";
import useWidgetSignals from "./dataset-signals.hook";
import WidgetHeader from "./Header";
import { IRunnableButton, ITimeSeries } from "../../api/schema.api";
import ajax from "@cayo/shared/libs/ajax";
import { dateRangeConverters } from "../../scheme/converters/date-range-converters";
import RunnableButton from "../Buttons/RunnableButton";
import { UserSettingsStore } from "../GridContainer/repo/UserSettingsStore";
import injectViewStorage from "../Hoc/inject-view-storage";
import Spacer from "../Utilities/Spacer";

const log = logger.getLogger("RecentChanges");
interface IRecentChangesSettings {
  selectedPeriod?: {
    [key: string]: number;
  };
}

const TimeSeries: FC<
  ITimeSeries & { viewStorage: IComponentViewStorage<IRecentChangesSettings> }
> = (props) => {
  const {
    lineChart,
    title,
    periodOptions,
    viewStorage,
    stick,
    url,
    name,
    viewAllTitle,
    runButton,
    userRoles,
  } = props;
  const [options, setOptions] = useState<IOfficeDropdownOption[]>(
    initOptions(periodOptions as IDropdownOption[], viewStorage.get(), name!)
  );
  const { officeTheme } = useTheme();
  const intl = useIntl();
  const { gridKey, targetType, path } = getViewInfo(name!);

  const history = useHistory();

  const { data, isLoading, error, reloadData, updateTime } = ajax.useGet(url!, {
    ...props,
    days: options.find((o) => o.selected)?.data || 0,
  });

  useWidgetSignals({
    signal: "refresh",
    handler: reloadData,
  });

  const handleChange = (e: FormEvent<HTMLDivElement>, option?: IOfficeDropdownOption) => {
    if (!option) {
      return;
    }

    options.forEach((element) => {
      element.selected = element.key === option.data;
    });

    setOptions([...options]);

    viewStorage.set({ selectedPeriod: { [name!]: option.data } });
  };

  const selectedPeriod = options.find((o) => o.selected)?.data || 0;

  const makeTick = useCallback(
    (period: number, d: any) => {
      if (!d[period]) {
        return "";
      }
      const periodTime = new Date(d[period].periodTime);
      // const periodTime = dateTimeUtils.parseTime(data.items[period].periodTime, false).value;
      return selectedPeriod === 0
        ? intl.formatRelativeTime(period - 60, "minute")
        : dateTimeUtils.periodToReadableString(periodTime!, selectedPeriod);
    },
    [selectedPeriod]
  );

  const gotoInterval = useCallback(
    async (interval: CustomInterval) => {
      log.debug("gotoInterval -> interval", interval);

      if (!targetType) {
        history.push(path);
        return;
      }

      const savedQuery: SavedQuery = {
        commonConditions: {
          queryTimeInterval: interval,
        },
        targetType,
      };
      const query = fromSavedQuery(savedQuery);

      log.debug("gotoInterval -> savedQuery, query", savedQuery, query);
      new UserSettingsStore(gridKey).saveQuery(query);
      history.push(path);
    },
    [targetType]
  );

  const areaSelectedHandler = useCallback(
    (data: { beginDate: number | Date; endDate: number | Date; selectedPeriod: any }) => {
      log.debug("areaSelectedHandler -> data, selectedPeriod, selectedPeriod.fromOptions", data);

      const hcrSettings = appSettings.viewSetting.getSubKey(gridKey) || {};
      delete hcrSettings?.filter;
      log.debug("areaSelectedHandler -> hcrSettings", hcrSettings);
      appSettings.viewSetting.deleteSubKey(gridKey);
      appSettings.viewSetting.updateSubKey(gridKey, hcrSettings);

      const beginDate = new Date(data.beginDate); // withTimezone(data[0]);
      let endDate = new Date(data.endDate);
      const selectedPeriod = data.selectedPeriod;

      if (beginDate.getTime() === endDate.getTime()) {
        if (selectedPeriod === 0) {
          //last hour
          //beginDate = new Date(beginDate.getTime() - 1 * 60 * 1000 );// -1m
          endDate = new Date(endDate.getTime() + 1 * 60 * 1000); // +1m
        } else if (selectedPeriod === 1) {
          //24 hours
          endDate = new Date(endDate.getTime() + 60 * 60 * 1000); // + 1h
        } else {
          //7 ... 180 days
          endDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000); // +1d
        }
      }

      if (selectedPeriod > 1) {
        beginDate.setTime(beginDate.getTime() + beginDate.getTimezoneOffset() * 60 * 1000);
        endDate.setTime(endDate.getTime() + endDate.getTimezoneOffset() * 60 * 1000);
      }

      const interval = dateRangeConverters.fromRange(beginDate, endDate);
      log.debug("interval, beginDate, endDate", interval, beginDate, endDate);
      gotoInterval(interval!);
    },
    []
  );

  const signals = useSignals<"areaSelected">([{ s: "areaSelected", handler: areaSelectedHandler }]);

  const location = useLocation();

  const showRunButton = useMemo(() => {
    const permitted = runButton?.permittedRole
      ? userRoles?.includes(runButton?.permittedRole)
      : true;

    return permitted;
  }, [location.pathname, runButton, userRoles]);

  return (
    <WidgetContainer stick={stick}>
      <WidgetHeader title={title} onRefresh={reloadData} updateTime={updateTime} />

      {isLoading && <LoadingOverlay />}
      {data?.length && (
        <LineChart
          {...lineChart}
          tickFormatter={makeTick}
          days={options.find((o) => o.selected)?.data || 0}
          data={data}
          onAreaSelected={(data) => signals.send("areaSelected", data)}
        />
      )}
      {!isLoading && !error && !data?.length && props.noItemsMessage}
      {error && (
        <FullHeightColumn>
          <AbsoluteEmptyContainer>
            <Text styles={{ root: { fontWeight: "bold" } }}>
              {intl.formatMessage(chartMessages.loadDataError)}
            </Text>
            <Text variant="small">{error}</Text>
          </AbsoluteEmptyContainer>
        </FullHeightColumn>
      )}
      <Stack
        verticalAlign={"center"}
        horizontalAlign={"stretch"}
        horizontal={true}
        className={css.mt12}
        onMouseDown={domUtils.stopMouseDown}
      >
        <Dropdown
          options={options}
          selectedKey={selectedPeriod}
          onChange={handleChange}
          styles={{
            dropdown: { minWidth: "140px" },
            label: { paddingRight: 4 },
            root: {
              alignSelf: "flex-end",
              display: "flex",

              right: "20px",
              top: 0,
            },
          }}
        />
        <Spacer />
        {runButton && showRunButton && (
          <RunnableButton
            {...(runButton as IRunnableButton)}
            onRefresh={reloadData}
            // eslint-disable-next-line no-inline-styles/no-inline-styles
            style={{ marginLeft: 8 }}
          />
        )}

        <ActionButton
          id="ViewAllChanges"
          text={""}
          styles={{ root: { color: officeTheme.semanticColors.link, height: "auto" } }}
          onClick={() => {
            const selectedPeriod = options.find((o) => o.selected);
            const interval = dateRangeConverters.rangeToType(selectedPeriod?.data);
            log.debug("ViewAllChanges.click -> selectedPeriod, interval", selectedPeriod, interval);

            gotoInterval(interval!);
          }}
        >
          {viewAllTitle ?? (
            <FormattedMessage id="recent-changes-chart.view-all" defaultMessage="View all" />
          )}
          <Icon
            iconName={"Forward"}
            styles={{
              root: {
                fontSize: 10,
                fontWeight: "bold",
                paddingLeft: 4,
                paddingTop: 2,
              },
            }}
          />
        </ActionButton>
      </Stack>
    </WidgetContainer>
  );
};

const initOptions = (
  periodOptions: IDropdownOption[],
  settings: IRecentChangesSettings,
  name: string
) => {
  const newOptions = periodOptions.map<IOfficeDropdownOption>((o) => ({
    data: o.value,
    key: o.value,
    selected: o.selected,
    text: o.text || "",
    title: o.text,
  }));

  if (
    settings &&
    settings.selectedPeriod !== undefined &&
    Boolean(Object.keys(settings.selectedPeriod).length)
  ) {
    newOptions.forEach((element) => {
      element.selected = element.key === settings.selectedPeriod?.[name];
    });
  } else {
    if (!newOptions.find((o) => o.selected)) {
      newOptions[0].selected = true;
    }
  }

  return newOptions;
};

const getViewInfo = (name: string) => {
  let gridKey = "";
  let targetType = "";
  let path = "";

  if (name === "threatsByLevel") {
    path = "/riskAssessment/rules";
  } else if (name === "threatAlertsByLevel") {
    gridKey = "riskAssessment/alertsctx_grid_grid";
    targetType = "cayo.graph.riskAlert";
    path = "/riskAssessment/alerts";
  } else {
    gridKey = "changeAudit/changehistoryctx_grid_grid";
    targetType = "cayo.graph.historyRecord";
    path = "/changeAudit/changehistory";
  }

  return { gridKey, targetType, path };
};

export default injectViewStorage(TimeSeries);
